import { useState } from 'react';
import { BookOpenIcon, ChevronDownIcon, KeyRoundIcon, MapIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { AIInsightPopover } from './AIInsightPopover';
type Pathway = {
  name: string;
  modules: string[];
  used: number;
  total: number;
};
type SkillModule = {
  name: string;
  used: number;
  total: number;
};
const pathways: Pathway[] = [{
  name: 'Digital Marketing',
  modules: ['SEO Foundations', 'Campaign Strategy', 'Analytics Essentials', 'Content Planning'],
  used: 4,
  total: 20
}, {
  name: 'AWS Solutions Architect',
  modules: ['AWS Core Services', 'Cloud Networking', 'Security & IAM', 'High Availability Architecture'],
  used: 8,
  total: 20
}];
const skillModules: SkillModule[] = [{
  name: 'Scrum Master Fundamentals Course',
  used: 20,
  total: 20
}, {
  name: 'IT Bundle',
  used: 75,
  total: 100
}, {
  name: 'Anatomy, Physiology and Coding I Course',
  used: 5,
  total: 20
}, {
  name: 'Human Resources Bundle',
  used: 80,
  total: 100
}];
export function SkillsOverview() {
  const [expandedPathway, setExpandedPathway] = useState('');
  return <motion.section initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.05
  }} data-phoebe-tour="skills-overview" aria-labelledby="skills-overview-title" className="mt-8 rounded-xl border border-gray-100 bg-white shadow-sm" data-id="element-2084" data-mp="3vx4sysyii67w">
      <header className="flex flex-col gap-4 border-b border-gray-100 px-5 py-5 sm:px-6 md:flex-row md:items-center md:justify-between" data-id="element-2085" data-mp="nhbl12i8y32g">
        <div className="flex items-start gap-3" data-id="element-2086" data-mp="ei81jxf63lnf">
          <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-[#30718D]/10 text-[#30718D]" data-id="element-2087" data-mp="egwqrdnb1dmt">
            <KeyRoundIcon size={19} aria-hidden="true" data-id="element-2088" data-mp="2z8yt0jcjc1zy" />
          </span>
          <div data-id="element-2089" data-mp="2za9yqopjjjtp">
            <div className="flex items-center gap-2" data-id="element-2090" data-mp="2z6m2viwmilbt">
              <h2 id="skills-overview-title" className="text-base font-bold text-gray-900" data-id="element-2091" data-mp="2u8uegyv6thcd">
                Skills Overview
              </h2>
              <AIInsightPopover align="left" headline="There is unused capacity to assign" insightBeforeMetric="You still have" highlightedMetric="70 skill module seats" insightAfterMetric=" and 30 pathway seats available." explanation="Only two pathways are in use, so grouping related skills into a pathway would make learner progress easier to track." insightId={13} data-id="element-2092" data-mp="2vp84jlvuoedq" />
            </div>
            <p className="mt-0.5 text-xs text-gray-500" data-id="element-2093" data-mp="232djamr7laz7">
              Review available capacity before assigning skills or pathways to learners.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 md:justify-end" data-id="element-2094" data-mp="1u1p77xldlkoy">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-[#FAF5FF] px-2.5 py-1 text-xs font-medium text-[#7E22CE]" data-id="element-2095" data-mp="1u27avfadb16w">
            <MapIcon size={13} aria-hidden="true" data-id="element-2096" data-mp="1z2ldght79ixj" />
            <strong data-id="element-2097" data-mp="1ffrt6dr0bov4">2</strong> Pathways <span className="text-[#A855F7]" data-id="element-2098" data-mp="1fh2xceod6ymr">· 10/40 used - 30 left</span>
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-md bg-[#EAF4F7] px-2.5 py-1 text-xs font-medium text-[#236B8C]" data-id="element-2099" data-mp="1ahqb9w5bhvl8">
            <BookOpenIcon size={13} aria-hidden="true" data-id="element-2100" data-mp="1ke34kswya7fg" />
            <strong data-id="element-2101" data-mp="1kciaf4ejsqi4">4</strong> Skill Modules <span className="text-[#5593B1]" data-id="element-2102" data-mp="1kgfubs8y9m32">· 170/240 used - 70 left</span>
          </span>
        </div>
      </header>

      <div className="max-h-[19rem] overflow-y-auto px-4 py-4 sm:px-5" data-id="element-2103" data-mp="1tf1d9oic334d">
        <div className="mb-3 flex items-center justify-between gap-4" data-id="element-2104" data-mp="1tcx7t24sdw1z">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500" data-id="element-2105" data-mp="2834igvojyas4">Skills Pathways</h3>
          <p className="hidden text-[11px] text-gray-400 sm:block" data-id="element-2106" data-mp="236nty0dfxuei">Select a pathway to view its skill modules</p>
        </div>
        <div className="space-y-1.5" data-id="element-2107" data-mp="2c59bbs76f3dp">
          {pathways.map(pathway => {
          const isExpanded = expandedPathway === pathway.name;
          const panelId = `pathway-${pathway.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
          return <div key={pathway.name} className="overflow-hidden rounded-lg border border-[#F3E8FF] bg-[#FAF5FF]" data-id="element-2108" data-mp="fdbyp3dt87f8">
                <button type="button" onClick={() => setExpandedPathway(isExpanded ? '' : pathway.name)} aria-expanded={isExpanded} aria-controls={panelId} className={`flex w-full items-center gap-2 px-3 py-2 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#A855F7] ${isExpanded ? 'bg-[#F3E8FF]' : 'hover:bg-[#F8F0FF]'}`} data-id="element-2109" data-mp="k8931x059sls">
                  <ChevronDownIcon size={16} aria-hidden="true" className={`flex-none text-[#A855F7] transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-2110" data-mp="5ijydrsuf6gr" />
                  <span className="min-w-0 flex-1" data-id="element-2111" data-mp="1gvmeklr15u0e">
                    <span className="text-sm font-semibold text-[#581C87]" data-id="element-2112" data-mp="1gub46332xo0b">{pathway.name}</span>
                    <span className="ml-3 text-[11px] text-[#A855F7]" data-id="element-2113" data-mp="1qzaw3wjj2za8">{pathway.modules.length} modules</span>
                  </span>
                  <span className="whitespace-nowrap text-xs font-medium text-[#A855F7]" data-id="element-2114" data-mp="1ha16cnc5pspa">
                    ({pathway.used}/{pathway.total} · {pathway.total - pathway.used} left)
                  </span>
                </button>
                <AnimatePresence initial={false} data-id="element-2115" data-mp="18amgnq3tdm5u">
                  {isExpanded && <motion.div id={panelId} initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.18
              }} className="overflow-hidden border-t border-[#F3E8FF] bg-white" data-id="element-2116" data-mp="13a8jj33r3wml">
                      <div className="p-3 sm:px-7" data-id="element-2117" data-mp="2onl36py68d87">
                        <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.05em] text-gray-500" data-id="element-2118" data-mp="2om07gx04eo8r">Included skill modules</p>
                        <div className="flex flex-wrap gap-1.5" data-id="element-2119" data-mp="qsfiqtxvttxs">
                          {pathway.modules.map(module => <span key={module} className="rounded bg-[#EAF4F7] px-2 py-1 text-xs font-medium text-[#236B8C]" data-id="element-2120" data-mp="d1spviesyy9m">
                              {module}
                            </span>)}
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>

        <h3 className="mb-2 mt-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500" data-id="element-2121" data-mp="vt3ajhtxqet2">Skill Modules</h3>
        <div className="flex flex-wrap gap-1.5" data-id="element-2122" data-mp="3gh7v86c8kjof">
          {skillModules.map(module => <span key={module.name} className="inline-flex items-center gap-1 rounded bg-[#EAF4F7] px-2 py-1 text-xs font-medium text-[#236B8C]" data-id="element-2123" data-mp="3lgazvpob0nvv">
              <span data-id="element-2124" data-mp="3cxza0pgmsajm">{module.name}</span>
              <span className="text-[#5593B1]" data-id="element-2125" data-mp="3d150a80zxigx">
                ({module.used}/{module.total} used · <span className={module.total - module.used === 0 ? 'font-semibold text-[#DB3725]' : ''} data-id="element-2126" data-mp="38xfajf8l4uub">{module.total - module.used} left</span>)
              </span>
            </span>)}
        </div>
      </div>
    </motion.section>;
}